import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resendVerificationEmail } from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import { getCurrentLocale, LOCALE_OPTIONS, setPreferredLocale, t } from '../i18n';
import './AuthPages.css';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const currentLocale = getCurrentLocale();
  const initialLocale = LOCALE_OPTIONS.some(option => option.value === currentLocale) ? currentLocale : 'en-GB';
  const [language, setLanguage] = useState(initialLocale);
  const { login, creator } = useAuth();
  const navigate = useNavigate();

  // When creator is set, the user will be automatically redirected by PublicRoute
  useEffect(() => {
    if (creator) {
      navigate('/');
    }
  }, [creator, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setResendCooldown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfoMessage('');
    setShowResendVerification(false);
    setLoading(true);

    try {
      await login(email, password);
      // Don't navigate manually - let PublicRoute handle it based on creator state change
    } catch (err: any) {
      const errorData = err.response?.data;
      const rawApiError = String(errorData?.error || '').toLowerCase();
      const unverifiedHint =
        !!errorData?.emailNotVerified || /not verified|verify your email|verifica tu correo|verific/.test(rawApiError);

      if (unverifiedHint) {
        setError(t('login.error.verifyEmail'));
        setShowResendVerification(true);
      } else {
        setError(t('login.error.invalidCredentials'));
      }
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email || resendLoading || resendCooldown > 0) {
      return;
    }

    try {
      setResendLoading(true);
      setError('');
      const response = await resendVerificationEmail(email);
      setInfoMessage(t('login.resend.success'));
      setResendCooldown(response.data?.retryAfterSeconds || 120);
    } catch (err: any) {
      const status = err.response?.status;
      const retryAfter = err.response?.data?.retryAfterSeconds;

      if (status === 429) {
        setResendCooldown(retryAfter || 120);
        setInfoMessage(t('login.resend.wait'));
      } else {
        setError(t('login.resend.error'));
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedLanguage = setPreferredLocale(event.target.value);
    setLanguage(selectedLanguage);
  };

  const shouldShowResendVerification = showResendVerification;

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-language-switcher">
            <label htmlFor="login-language-select">{t('navbar.languageLabel')}</label>
            <select
              id="login-language-select"
              value={language}
              onChange={handleLanguageChange}
            >
              {LOCALE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
          </div>
          <img src="/logo.webp" alt="X-periences" className="auth-logo" />
          <h1 className="auth-title">{t('login.title')}</h1>
          <p className="auth-subtitle">{t('login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="auth-error">{error}</div>}
          {infoMessage && <div className="form-success">{infoMessage}</div>}
          
          <Input
            label={t('common.email')}
            type="email"
            placeholder={t('common.emailExample')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <Input
            label={t('common.password')}
            type="password"
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <div style={{ textAlign: 'right', marginTop: '-8px' }}>
            <Link to="/forgot-password" style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              {t('login.forgotPassword')}
            </Link>
          </div>

          <Button type="submit" fullWidth loading={loading}>
            {t('login.submit')}
          </Button>

          {shouldShowResendVerification && (
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={handleResendVerification}
              loading={resendLoading}
              disabled={resendCooldown > 0 || resendLoading || !email}
            >
              {resendCooldown > 0
                ? `${t('login.resend.waitWithSeconds')} ${resendCooldown}s`
                : t('login.resend.button')}
            </Button>
          )}
        </form>

        <div className="auth-footer">
          {t('login.noAccount')}{' '}
          <Link to="/register" className="auth-link">{t('login.createOne')}</Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
